import { Api } from "../../../../Api";
import { DataFrame, Method, Status } from "../../../../DataFrame";
import { DependencyContainer } from "../../../../DependencyContainer";
import { ExalusConnectionService } from "../../../ExalusConnectionService";
import { ResponseResult } from "../../../FieldChangeResult";
import { IExalusConnectionService } from "../../../IExalusConnectionService";
import { ILoggerService } from "../../../Logging/ILoggerService";
import { LoggerService } from "../../../Logging/LoggerService";
import { ChatService, ChatServiceErrorCode } from "../../ChatService";
import { IMessage, MessageHandler } from "../../IChatService";
import { ControllerChatSender, ControllerMessageActions, ControllerMessageSource, ControllerMessageType, IControllerChat, IControllerChatMessage } from "./IControllerChat";

export class ControllerChat implements IControllerChat {

    private _connection: IExalusConnectionService;
    private _chatSubscriptions: MessageHandler<any>[] = [];

    constructor() {
        this._connection = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);

        let removeSubCallback = this._connection.SubscribeTo<ControllerChatMessageResponse>("/homemessaging/notify/message/new", (frame) => {
            const data = frame.Data as ControllerChatMessageResponse;

            //messages for 'family' chat
            if (data.RecipientUserGuid == null && data.MessageType == ControllerMessageType.Message)
                return;

            const res = new ControllerChatMessage();

            res.DateTime = data.DateTime;
            res.Guid = data.Guid;
            res.MessageSource = data.MessageSource;
            res.MessageText = data.MessageText;
            res.MessageType = data.MessageType;
            res.RecipientUserGuid = data.RecipientUserGuid ? data.RecipientUserGuid : "n/a";
            res.Sender = data.Sender;
            res.SenderName = data.SenderName ? data.SenderName : "n/a";
            res.SenderUserGuid = data.SenderUserGuid ? data.SenderUserGuid : "n/a";
            res.TextToSpeechMessage = data.TextToSpeechMessage ? data.TextToSpeechMessage : ""

            for (let [k, v] of Object.entries(data.MessageActions))
                res.MessageActions.set(ControllerMessageActions[k as keyof typeof ControllerMessageActions], v as number);

            for (let [k, v] of Object.entries(data.MessageActionsData))
                res.MessageActionsData.set(k, v);

            this._chatSubscriptions.forEach(handler => {
                handler(res);
            })
        });
    }

    async GetCachedMessagesAsync(): Promise<IControllerChatMessage[] | ResponseResult<ChatServiceErrorCode>> {
        try {
            const resp = await this._connection.SendAndWaitForResponseAsync<ControllerChatMessageResponse[]>(new GetMessagesRequest(), 12000, false);

            if (resp == null)
                return new ResponseResult(ChatServiceErrorCode.OtherError, `Cannot get messages - response is null.`);

            if (resp.Status != Status.OK)
                return new ResponseResult(ChatServiceErrorCode.OtherError, `Cannot get messages - controller responded with response code ${resp.Status}.`);

            if (resp.Data == null)
                return new ResponseResult(ChatServiceErrorCode.NoData, `Cannot get messages - controller responded with status OK, but response does not contain data!`);


            const result: ControllerChatMessage[] = [];

            resp.Data.forEach(d => {

                //messages for 'family' chat
                if (d.RecipientUserGuid == null && d.MessageType == ControllerMessageType.Message)
                    return;

                const res = new ControllerChatMessage();

                res.DateTime = d.DateTime;
                res.Guid = d.Guid;
                res.MessageSource = d.MessageSource;
                res.MessageText = d.MessageText;
                res.MessageType = d.MessageType;
                res.RecipientUserGuid = d.RecipientUserGuid ? d.RecipientUserGuid : "n/a";
                res.Sender = d.Sender;
                res.SenderName = d.SenderName ? d.SenderName : "n/a";
                res.SenderUserGuid = d.SenderUserGuid ? d.SenderUserGuid : "n/a";
                res.TextToSpeechMessage = d.TextToSpeechMessage ? d.TextToSpeechMessage : ""

                for (let [k, v] of Object.entries(d.MessageActions))
                    res.MessageActions.set(ControllerMessageActions[k as keyof typeof ControllerMessageActions], v as number);

                for (let [k, v] of Object.entries(d.MessageActionsData))
                    res.MessageActionsData.set(k, v);

                result.push(res);
            });

            return result;
        } catch (error) {
            return new ResponseResult(ChatServiceErrorCode.FatalError, `Cannot get messages - exception occurred ${error}`);
        }
    }

    async SendMessageAsync(message: string): Promise<Status> {
        try {
            const result = await this._connection.SendAndWaitForResponseAsync<ControllerChatMessageResponse[]>(new SendMessagesRequest(`recipient:home ${message}`), 8000, false);

            if (result == null || result.Status == null)
                return Status.FatalError;

            return result.Status;

        } catch (error) {
            DependencyContainer.Log?.Error(ChatService.ServiceName, `Cannot send message! ${error}`);
            return Status.FatalError;
        }
    }

    SubscribeToMessages<T extends IMessage>(messageHandler: MessageHandler<T>): void {
        this._chatSubscriptions.push(messageHandler);
    }

    UnsubscribeFromMessages<T extends IMessage>(messageHandler: MessageHandler<T>): void {
        if (this._chatSubscriptions)
            this._chatSubscriptions = this._chatSubscriptions.filter(h => h !== messageHandler);
    }
}

class ControllerChatMessageResponse {
    Guid: string = "";
    Sender: ControllerChatSender = ControllerChatSender.Controller;
    MessageType: ControllerMessageType = ControllerMessageType.Info;
    MessageSource: ControllerMessageSource = ControllerMessageSource.Other;
    SenderUserGuid: string = "";
    RecipientUserGuid: string = "";
    MessageText: string = "";
    TextToSpeechMessage: string = "";
    SenderName: string = "";
    DateTime: string = new Date().toDateString();
    MessageActions: any;
    MessageActionsData: any;
}

class ControllerChatMessage implements IControllerChatMessage {
    private _sender: ControllerChatSender = ControllerChatSender.Controller;
    private _messageType: ControllerMessageType = ControllerMessageType.Info;
    private _messageSource: ControllerMessageSource = ControllerMessageSource.Other;
    private _senderUserGuid: string = "";
    private _recipientUserGuid: string = "";
    private _textToSpeechMessage: string = "";
    private _messageActions = new Map<ControllerMessageActions, number>();
    private _messageActionsData = new Map<string, any>();
    private _guid: string = "";
    private _senderName: string = "";
    private _messageText: string = "";
    private _dateTime = new Date().toDateString();

    get Sender(): ControllerChatSender {
        return this._sender;
    }
    get MessageType(): ControllerMessageType {
        return this._messageType;
    }
    get MessageSource(): ControllerMessageSource {
        return this._messageSource;
    }
    get SenderUserGuid(): string {
        return this._senderUserGuid;
    }
    get RecipientUserGuid(): string {
        return this._recipientUserGuid;
    }
    get TextToSpeechMessage(): string {
        return this._textToSpeechMessage;
    }
    get MessageActions(): Map<ControllerMessageActions, number> {
        return this._messageActions;
    }
    get MessageActionsData(): Map<string, any> {
        return this._messageActionsData;
    }
    get Guid(): string {
        return this._guid;
    }
    get SenderName(): string {
        return this._senderName;
    }
    get MessageText(): string {
        return this._messageText;
    }
    get DateTime(): string {
        return this._dateTime;
    }

    set Sender(value: ControllerChatSender) {
        this._sender = value;
    }
    set MessageType(value: ControllerMessageType) {
        this._messageType = value;
    }
    set MessageSource(value: ControllerMessageSource) {
        this._messageSource = value;
    }
    set SenderUserGuid(value: string) {
        this._senderUserGuid = value;
    }
    set RecipientUserGuid(value: string) {
        this._recipientUserGuid = value;
    }
    set TextToSpeechMessage(value: string) {
        this._textToSpeechMessage = value;
    }
    set MessageActions(value: Map<ControllerMessageActions, number>) {
        this._messageActions = value;
    }
    set MessageActionsData(value: Map<string, any>) {
        this._messageActionsData = value;
    }
    set Guid(value: string) {
        this._guid = value;
    }
    set SenderName(value: string) {
        this._senderName = value;
    }
    set MessageText(value: string) {
        this._messageText = value;
    }
    set DateTime(value: string) {
        this._dateTime = value;
    }
}

//Requests
class GetMessagesRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/homemessaging/messages";
        this.Method = Method.Get;
    }
}

class SendMessagesRequest extends DataFrame<string> {
    constructor(message: string) {
        super();
        this.Resource = "/homemessaging/message";
        this.Method = Method.Put;
        this.Data = message;
    }
}